/**
 * NetworkBanner: network-status awareness. Because the whole tool runs on the
 * device, going offline pauses nothing; the banner says so plainly rather than
 * warning. role=status + aria-live so screen readers hear the transition.
 */
import { useEffect, useState } from "react";
import { useVerificationMode } from "../../hooks/useVerificationMode";
import { useT, type Locale } from "../i18n";

/**
 * Localized chrome for the offline banner. Aligns with the verification-mode
 * idiom in Settings: every check runs on this device, the network is never
 * needed. Forensic data is untouched here, this is privacy reassurance copy.
 * `dns` is the honesty rider for the one mode that DOES use the network:
 * with live DNS checks on, "never needs the network" would be a lie.
 */
const COPY: Record<Locale, { base: string; dns: string }> = {
  en: {
    base: "You’re offline, and that changes nothing here. Every check runs on this device.",
    dns: "Live DNS lookups will wait until you are back online; everything else is unaffected.",
  },
  de: {
    base: "Du bist offline. Das ändert hier nichts: jede Prüfung läuft auf diesem Gerät.",
    dns: "Live-DNS-Abfragen warten, bis du wieder online bist; alles andere bleibt unberührt.",
  },
};

/** Closing reassurance when no mode ever touches the network. */
const NEVER: Record<Locale, string> = {
  en: "MIME Smith does not need a network connection.",
  de: "MIME Smith benötigt keine Netzverbindung.",
};

export function NetworkBanner() {
  const { locale } = useT();
  const { mode } = useVerificationMode();
  const copy = COPY[locale] ?? COPY.en;
  const c = `${copy.base} ${
    mode === "dns-enabled" ? copy.dns : (NEVER[locale] ?? NEVER.en)
  }`;
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // The live region stays mounted and the banner toggles inside it: screen
  // readers only announce insertions into an ALREADY-rendered live region.
  return (
    <div role="status" aria-live="polite">
      {online ? null : (
        <div className="nx-netbanner">
          <span className="nx-netbanner__dot" aria-hidden="true" />
          {c}
        </div>
      )}
    </div>
  );
}
